import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, DollarSign, Clock, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDate } from '@/lib/localizedDateUtils';
import { FinancePageLayout } from '@/components/finance';
import { useBlueVizaConversions } from '@/hooks/finance/useBlueVizaConversions';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const formatDate = (dateStr: string) => {
  return format(parseDate(dateStr)!, 'dd/MM/yyyy', { locale: ptBR });
};

export default function BlueVizaConversionsReport() {
  const { data: conversions, isLoading } = useBlueVizaConversions();

  // Calculate summary stats
  const totalConversions = conversions?.length || 0;
  const totalRevenue = conversions?.reduce((sum, c) => sum + c.divus_amount, 0) || 0;
  const avgDays = conversions?.length 
    ? conversions.reduce((sum, c) => sum + c.dias_para_conversao, 0) / conversions.length
    : 0;

  return (
    <FinancePageLayout>
      <h1 className="text-2xl font-bold tracking-tight">Conversões BlueViza → Divus</h1>

      {isLoading ? (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-[120px]" />
            ))}
          </div>
          <Skeleton className="h-[400px]" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Conversões</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalConversions}</div>
                <p className="text-xs text-muted-foreground">
                  Clientes BlueViza que fecharam Divus
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Receita Gerada</CardTitle>
                <DollarSign className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(totalRevenue)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Valor total dos deals Divus
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tempo Médio Conversão</CardTitle>
                <Clock className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {avgDays.toFixed(0)} dias
                </div>
                <p className="text-xs text-muted-foreground">
                  Entre deal BlueViza e Divus
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Conversions Table */}
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Conversões</CardTitle>
            </CardHeader>
            <CardContent>
              {!conversions || conversions.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  Nenhuma conversão encontrada
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Deal BlueViza</TableHead>
                      <TableHead className="text-center"></TableHead>
                      <TableHead>Deal Divus</TableHead>
                      <TableHead className="text-center">Dias p/ Conversão</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conversions.map((conversion) => (
                      <TableRow key={conversion.divus_deal_id}>
                        <TableCell className="font-medium">
                          {conversion.cliente}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm">{conversion.blueviza_deal_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(conversion.blueviza_closed_date)}
                            </span>
                            <span className="text-sm text-blue-600">
                              {formatCurrency(conversion.blueviza_amount)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <ArrowRight className="h-5 w-5 text-muted-foreground mx-auto" />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm">{conversion.divus_deal_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(conversion.divus_closed_date)}
                            </span>
                            <span className="text-sm text-green-600 font-medium">
                              {formatCurrency(conversion.divus_amount)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            variant={conversion.dias_para_conversao <= 30 ? "default" : "secondary"}
                          >
                            {conversion.dias_para_conversao} dias
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </FinancePageLayout>
  );
}
